import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity,
  GitBranch,
  Shield,
  FileCheck,
  Play,
  AlertTriangle,
  CheckCircle,
  MessageSquare,
  LogOut,
  DollarSign,
  Clock,
  X,
  Workflow,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useProjectActivity } from "@/hooks/useProjectActivity";
import type { ActivityEvent } from "@/hooks/useProjectActivity";
import { formatRelativeTime, formatDateTime } from "@/lib/date";
import {
  type ActivityCategory,
  CATEGORY_META,
  categorize,
  computeDigestSummary,
  formatCost,
  formatDuration,
} from "@/lib/activityUtils";

// ---------------------------------------------------------------------------
// Icon mapping
// ---------------------------------------------------------------------------

const EVENT_ICONS: Record<string, typeof Activity> = {
  "pipeline.started": Play,
  "pipeline.completed": FileCheck,
  "pipeline.failed": AlertTriangle,
  "gate.passed": Shield,
  "gate.failed": AlertTriangle,
  work_lifecycle: GitBranch,
  pipeline_phase_completed: FileCheck,
  token_usage_daily: DollarSign,
  token_usage: DollarSign,
  story_cost_summary: DollarSign,
  session_closed: LogOut,
  session_started: Play,
  phase_finish: CheckCircle,
  session_topic: MessageSquare,
};

function eventIcon(eventType: string) {
  return EVENT_ICONS[eventType] ?? Activity;
}

// ---------------------------------------------------------------------------
// Category label for table
// ---------------------------------------------------------------------------

const CATEGORY_SHORT: Record<ActivityCategory, string> = {
  development: "Dev",
  pipeline: "Pipeline",
  costs: "Cost",
};

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function ActivitySkeleton() {
  return (
    <div data-testid="activity-skeleton" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-lg border bg-muted/50"
          />
        ))}
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 5 }, (_, i) => (
          <div
            key={i}
            className="h-8 w-20 animate-pulse rounded-full bg-muted/50"
          />
        ))}
      </div>
      <div className="space-y-0">
        {Array.from({ length: 8 }, (_, i) => (
          <div
            key={i}
            className="h-10 animate-pulse border-b border-border/30 bg-muted/20"
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary bar — compact stat cards
// ---------------------------------------------------------------------------

function SummaryBar({
  totalEvents,
  storiesActive,
  sessionsToday,
  pipelinePhases,
}: {
  totalEvents: number;
  storiesActive: number;
  sessionsToday: number;
  pipelinePhases: number;
}) {
  const stats = [
    { label: "Events", value: totalEvents, icon: Activity, color: "text-foreground" },
    { label: "Work Items", value: storiesActive, icon: GitBranch, color: "text-blue-400" },
    { label: "Sessions", value: sessionsToday, icon: MessageSquare, color: "text-emerald-400" },
    { label: "Pipeline", value: pipelinePhases, icon: Workflow, color: "text-purple-400" },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((s) => {
        const Icon = s.icon;
        return (
          <div
            key={s.label}
            className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3"
          >
            <Icon className={cn("h-4 w-4 shrink-0", s.color)} />
            <div className="flex items-baseline gap-2">
              <span className={cn("text-xl font-bold tabular-nums", s.color)}>
                {s.value}
              </span>
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category filter tabs (OpenRouter style)
// ---------------------------------------------------------------------------

type FilterValue = "all" | ActivityCategory;

function CategoryTabs({
  active,
  onChange,
  counts,
}: {
  active: FilterValue;
  onChange: (v: FilterValue) => void;
  counts: Record<FilterValue, number>;
}) {
  const tabs: { value: FilterValue; label: string }[] = [
    { value: "all", label: "All" },
    { value: "development", label: "Development" },
    { value: "pipeline", label: "Pipeline" },
    { value: "costs", label: "Costs" },
  ];

  return (
    <div className="flex items-center gap-1 border-b border-border">
      {tabs.map((tab) => {
        const isActive = active === tab.value;
        const count = counts[tab.value];
        return (
          <button
            key={tab.value}
            onClick={() => onChange(tab.value)}
            className={cn(
              "relative px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
            {count > 0 && (
              <span
                className={cn(
                  "ml-1.5 tabular-nums text-xs",
                  isActive ? "text-primary/70" : "opacity-50",
                )}
              >
                {count}
              </span>
            )}
            {isActive && (
              <div className="absolute inset-x-0 -bottom-px h-0.5 bg-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel (slide-in from right, OpenRouter style)
// ---------------------------------------------------------------------------

function DetailPanel({
  event,
  onClose,
}: {
  event: ActivityEvent;
  onClose: () => void;
}) {
  const Icon = eventIcon(event.event_type);
  const cat = categorize(event.event_type);
  const meta = CATEGORY_META[cat];
  const payload = event.payload;

  const detailRows: { label: string; value: string }[] = [];

  if (payload) {
    if (payload.work_type) detailRows.push({ label: "Work Type", value: payload.work_type });
    if (payload.work_id) detailRows.push({ label: "Work ID", value: payload.work_id });
    if (payload.jira_key) detailRows.push({ label: "Jira Key", value: payload.jira_key });
    if (payload.issue) detailRows.push({ label: "Issue", value: payload.issue });
    if (payload.phase) detailRows.push({ label: "Phase", value: payload.phase });
    if (payload.event) detailRows.push({ label: "Event", value: payload.event });
    if (payload.pipeline_name) detailRows.push({ label: "Pipeline", value: payload.pipeline_name });
    if (payload.branch) detailRows.push({ label: "Branch", value: payload.branch });
    if (payload.repo_slug) detailRows.push({ label: "Repository", value: payload.repo_slug });
    if (payload.cost_usd != null) detailRows.push({ label: "Cost", value: formatCost(payload.cost_usd) });
    if (payload.duration_seconds != null) detailRows.push({ label: "Duration", value: formatDuration(payload.duration_seconds) });
    if (payload.run_id) detailRows.push({ label: "Run ID", value: payload.run_id });
    if (payload.gate_type) detailRows.push({ label: "Gate Type", value: payload.gate_type });
    if (payload.gate_result) detailRows.push({ label: "Gate Result", value: payload.gate_result });
    if (payload.phase_next) detailRows.push({ label: "Next Phase", value: payload.phase_next });
    if (payload.agent_session_id) detailRows.push({ label: "Session ID", value: payload.agent_session_id });
    if (payload.tool_fail_ratio != null) detailRows.push({ label: "Tool Fail Ratio", value: `${(payload.tool_fail_ratio * 100).toFixed(1)}%` });
    if (payload.max_gate_fail_streak != null) detailRows.push({ label: "Max Gate Fail Streak", value: String(payload.max_gate_fail_streak) });
    if (payload.edit_revert_files != null) detailRows.push({ label: "Edit/Revert Files", value: String(payload.edit_revert_files) });
  }

  return (
    <div className="flex h-full flex-col border-l border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Event details</span>
          <span className={cn("rounded px-1.5 py-0.5 text-xs font-medium", meta.bgClass, meta.colorClass)}>
            {CATEGORY_SHORT[cat]}
          </span>
        </div>
        <button onClick={onClose} className="rounded p-1 hover:bg-muted">
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Title section */}
      <div className="border-b border-border px-4 py-4">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-5 w-5", meta.colorClass)} />
          <h3 className="text-sm font-semibold">{event.title}</h3>
        </div>
        {event.summary && (
          <p className="mt-2 text-sm text-muted-foreground">{event.summary}</p>
        )}
        <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
          <time dateTime={event.recorded_at} title={formatDateTime(event.recorded_at)}>
            {formatDateTime(event.recorded_at)}
          </time>
          {event.actor && <span>{event.actor}</span>}
        </div>
      </div>

      {/* Key metrics (if present) */}
      {payload && (payload.cost_usd != null || payload.duration_seconds != null) && (
        <div className="grid grid-cols-2 gap-px border-b border-border bg-border">
          {payload.cost_usd != null && (
            <div className="flex flex-col items-center gap-1 bg-card px-4 py-3">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <DollarSign className="h-3 w-3" /> Cost
              </div>
              <span className="text-lg font-bold tabular-nums text-amber-400">
                {formatCost(payload.cost_usd)}
              </span>
            </div>
          )}
          {payload.duration_seconds != null && (
            <div className="flex flex-col items-center gap-1 bg-card px-4 py-3">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" /> Duration
              </div>
              <span className="text-lg font-bold tabular-nums">
                {formatDuration(payload.duration_seconds)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Detail rows */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="space-y-2">
          {detailRows.map((row) => (
            <div key={row.label} className="flex items-baseline justify-between gap-3">
              <span className="text-xs text-muted-foreground">{row.label}</span>
              <span className="text-right text-xs font-mono text-foreground truncate max-w-[60%]">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------

function EventRow({
  event,
  isSelected,
  onClick,
}: {
  event: ActivityEvent;
  isSelected: boolean;
  onClick: () => void;
}) {
  const Icon = eventIcon(event.event_type);
  const cat = categorize(event.event_type);
  const meta = CATEGORY_META[cat];
  const payload = event.payload;

  const costDisplay = payload?.cost_usd != null ? formatCost(payload.cost_usd) : null;
  const durationDisplay = payload?.duration_seconds != null
    ? formatDuration(payload.duration_seconds)
    : null;

  return (
    <tr
      onClick={onClick}
      className={cn(
        "group cursor-pointer border-b border-border/30 transition-colors",
        isSelected ? "bg-muted/60" : "hover:bg-muted/30",
      )}
    >
      {/* Date */}
      <td className="whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground">
        {formatRelativeTime(event.recorded_at)}
      </td>
      {/* Category icon + type */}
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-3.5 w-3.5 shrink-0", meta.colorClass)} />
          <span className={cn("text-xs font-medium", meta.colorClass)}>
            {CATEGORY_SHORT[cat]}
          </span>
        </div>
      </td>
      {/* Title + summary */}
      <td className="max-w-0 px-3 py-2.5">
        <p className="truncate text-sm text-foreground">{event.title}</p>
        {event.summary && (
          <p className="truncate text-xs text-muted-foreground">{event.summary}</p>
        )}
      </td>
      {/* Cost */}
      <td className="whitespace-nowrap px-3 py-2.5 text-right">
        {costDisplay && (
          <span className="text-xs font-semibold tabular-nums text-amber-400">
            {costDisplay}
          </span>
        )}
      </td>
      {/* Duration */}
      <td className="whitespace-nowrap px-3 py-2.5 text-right">
        {durationDisplay && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {durationDisplay}
          </span>
        )}
      </td>
      {/* Actor */}
      <td className="hidden whitespace-nowrap px-3 py-2.5 text-xs text-muted-foreground xl:table-cell">
        {event.actor ? (
          <span className="truncate">{event.actor}</span>
        ) : null}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Infinite scroll hook
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;

function useInfiniteScroll(totalItems: number) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, totalItems));
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [totalItems]);

  return { visibleCount, sentinelRef };
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function ProjectActivityPanel() {
  const { projectSlug = "" } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation();
  const { events, digest, total, loading, error, refetch } =
    useProjectActivity(projectSlug);
  const [activeFilter, setActiveFilter] = useState<FilterValue>("all");
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);

  const summary = useMemo(
    () => computeDigestSummary(digest, events, total),
    [digest, events, total],
  );

  const categoryCounts = useMemo(() => {
    const counts: Record<FilterValue, number> = {
      all: events.length,
      development: 0,
      pipeline: 0,
      costs: 0,
    };
    for (const ev of events) {
      counts[categorize(ev.event_type)]++;
    }
    return counts;
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (activeFilter === "all") return events;
    return events.filter((e) => categorize(e.event_type) === activeFilter);
  }, [events, activeFilter]);

  const { visibleCount, sentinelRef } = useInfiniteScroll(filteredEvents.length);
  const visibleEvents = filteredEvents.slice(0, visibleCount);

  const handleRowClick = useCallback((event: ActivityEvent) => {
    setSelectedEvent((prev) =>
      prev?.event_id === event.event_id && prev?.recorded_at === event.recorded_at
        ? null
        : event,
    );
  }, []);

  if (loading) return <ActivitySkeleton />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12">
        <p className="text-sm text-destructive">
          {t("projectPortal.activity.loadError")}
        </p>
        <button
          onClick={() => refetch()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t("projectPortal.activity.retry")}
        </button>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12">
        <p className="text-sm font-medium">
          {t("projectPortal.activity.empty")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("projectPortal.activity.emptyDetail")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <SummaryBar
        totalEvents={summary.totalEvents}
        storiesActive={summary.storiesActive}
        sessionsToday={summary.sessionsToday}
        pipelinePhases={summary.pipelinePhases}
      />

      {/* Category tabs */}
      <CategoryTabs
        active={activeFilter}
        onChange={setActiveFilter}
        counts={categoryCounts}
      />

      {/* Table + detail panel */}
      <div className="flex gap-0 overflow-hidden rounded-lg border">
        {/* Table area */}
        <div className={cn("flex-1 overflow-y-auto", selectedEvent ? "max-h-[65vh]" : "max-h-[70vh]")}>
          <table className="w-full">
            <thead className="sticky top-0 z-10 bg-muted/80 backdrop-blur-sm">
              <tr className="border-b border-border text-xs font-medium text-muted-foreground">
                <th className="whitespace-nowrap px-3 py-2 text-left">Date</th>
                <th className="whitespace-nowrap px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="whitespace-nowrap px-3 py-2 text-right">Cost</th>
                <th className="whitespace-nowrap px-3 py-2 text-right">Duration</th>
                <th className="hidden whitespace-nowrap px-3 py-2 text-left xl:table-cell">Actor</th>
              </tr>
            </thead>
            <tbody>
              {visibleEvents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
                    No events in this category
                  </td>
                </tr>
              ) : (
                visibleEvents.map((event) => (
                  <EventRow
                    key={event.event_id ?? event.recorded_at}
                    event={event}
                    isSelected={
                      selectedEvent?.event_id === event.event_id &&
                      selectedEvent?.recorded_at === event.recorded_at
                    }
                    onClick={() => handleRowClick(event)}
                  />
                ))
              )}
            </tbody>
          </table>
          {/* Infinite scroll sentinel */}
          {visibleCount < filteredEvents.length && (
            <div ref={sentinelRef} className="flex items-center justify-center py-4">
              <span className="text-xs text-muted-foreground">
                Showing {visibleCount} of {filteredEvents.length}
              </span>
            </div>
          )}
        </div>

        {/* Detail panel (right side) */}
        {selectedEvent && (
          <div className="hidden w-80 shrink-0 sm:block lg:w-96">
            <DetailPanel
              event={selectedEvent}
              onClose={() => setSelectedEvent(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
